import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/categories";
import { Briefcase, MapPin, Calendar as CalIcon, DollarSign, Send, Users2, Check, X as XIcon, Trash2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export const CastingDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [casting, setCasting] = useState(null);
  const [myApp, setMyApp] = useState(null);
  const [applications, setApplications] = useState([]);
  const [message, setMessage] = useState("");
  const [applying, setApplying] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/castings/${id}`);
    setCasting(data);
    if (user) {
      try {
        const apps = await api.get("/me/applications");
        const mine = apps.data.find((a) => a.casting_id === id);
        setMyApp(mine);
      } catch {}
      if (data.recruiter_id === user.user_id) {
        try {
          const r = await api.get(`/castings/${id}/applications`);
          setApplications(r.data);
        } catch {}
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

  if (!casting) return <div className="p-12 text-white/60">Chargement...</div>;
  const isRecruiter = user?.user_id === casting.recruiter_id;
  const cat = CATEGORIES.find((x) => x.key === casting.category);

  const apply = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/castings/${id}/apply`, { message });
      setMyApp(data);
      setMessage("");
      toast.success("Candidature envoyée !");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setApplying(false); }
  };

  const setAppStatus = async (appId, status) => {
    await api.patch(`/castings/${id}/applications/${appId}`, { status });
    setApplications(applications.map((a) => a.application_id === appId ? { ...a, status } : a));
    toast.success(status === "accepted" ? "Candidat retenu" : "Candidature déclinée");
  };

  const contactApplicant = async (app) => {
    try {
      const { data } = await api.post("/conversations", {
        to_user_id: app.user_id || app.applicant_id,
        body: `Bonjour ${app.applicant_name}, votre candidature a été retenue pour "${casting?.title}". On en discute ?`,
        context_type: "casting",
        context_id: id,
      });
      navigate(`/messages/${data.conversation_id}`);
    } catch (e) {
      toast.error("Impossible de démarrer la conversation");
    }
  };

  const closeCasting = async () => {
    if (!window.confirm("Fermer cette annonce ? Les candidats ne pourront plus postuler.")) return;
    await api.patch(`/castings/${id}`, { status: "closed" });
    setCasting({ ...casting, status: "closed" });
  };

  const removeCasting = async () => {
    if (!window.confirm("Supprimer définitivement cette annonce ?")) return;
    await api.delete(`/castings/${id}`);
    navigate("/castings");
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-5xl mx-auto pb-32" data-testid="casting-detail-page">
      <Link to="/castings" className="text-sm text-white/50 hover:text-white mb-4 inline-flex items-center gap-1">
        ← Retour aux opportunités
      </Link>

      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/10 to-brand-orange/5 p-7 md:p-10 mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange bg-brand-orange/10 border border-brand-orange/20 rounded-full px-3 py-1">
            {cat?.emoji} {cat?.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{casting.opportunity_type}</span>
          {casting.status !== "open" && (
            <span className="tag-pill bg-red-500/20 border-red-500/40 text-red-300">Fermée</span>
          )}
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter mb-3">{casting.title}</h1>
        <p className="text-white/70 mb-6 whitespace-pre-wrap">{casting.description}</p>

        {casting.requirements && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-6">
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-yellow font-bold mb-2">Critères recherchés</p>
            <p className="text-sm text-white/80 whitespace-pre-wrap">{casting.requirements}</p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
          <InfoBlock icon={<MapPin size={14} />} label="Lieu" value={`${casting.city || "—"}, ${casting.country}`} />
          {casting.deadline && <InfoBlock icon={<CalIcon size={14} />} label="Deadline" value={new Date(casting.deadline).toLocaleDateString("fr-FR")} />}
          {casting.budget && <InfoBlock icon={<DollarSign size={14} />} label="Budget" value={casting.budget} />}
          <InfoBlock icon={<Users2 size={14} />} label="Candidatures" value={casting.applications_count} />
        </div>

        <p className="text-xs text-white/50 mt-4">Publiée par <strong className="text-white/80">{casting.recruiter_name}</strong></p>
      </div>

      {isRecruiter && (
        <div className="mb-8" data-testid="recruiter-panel">
          <div className="flex flex-wrap gap-2 mb-5">
            {casting.status === "open" && (
              <button onClick={closeCasting} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="close-casting-btn">Fermer l'annonce</button>
            )}
            <button onClick={removeCasting} className="btn-ghost inline-flex items-center gap-2 text-red-400 text-sm" data-testid="delete-casting-btn">
              <Trash2 size={14} /> Supprimer
            </button>
            <a href={`mailto:${casting.contact_email}`} className="btn-ghost inline-flex items-center gap-2 text-sm"><Mail size={14} /> {casting.contact_email}</a>
          </div>
          <h2 className="font-display text-2xl font-black mb-5">Candidatures ({applications.length})</h2>
          {applications.length === 0 ? (
            <p className="text-white/60 text-sm">Pas encore de candidature.</p>
          ) : (
            <div className="space-y-3">
              {applications.map((a) => (
                <div key={a.application_id} className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`application-${a.application_id}`}>
                  <Link to={`/artist/${a.applicant_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center text-white font-bold shrink-0">
                      {a.applicant_picture ? <img src={a.applicant_picture} className="w-full h-full rounded-full object-cover" alt="" /> : a.applicant_name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{a.applicant_name}</p>
                      <p className="text-xs text-white/50 truncate">{a.applicant_country || "—"} · {a.applicant_genre || "—"}</p>
                    </div>
                  </Link>
                  {a.message && <p className="text-sm text-white/70 flex-1 md:max-w-md line-clamp-2">"{a.message}"</p>}
                  <div className="flex items-center gap-2">
                    {a.status === "pending" ? (
                      <>
                        <button onClick={() => setAppStatus(a.application_id, "accepted")} className="px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-semibold inline-flex items-center gap-1" data-testid={`accept-${a.application_id}`}>
                          <Check size={12} /> Accepter
                        </button>
                        <button onClick={() => setAppStatus(a.application_id, "rejected")} className="px-3 py-1.5 rounded-full bg-white/5 text-white/60 border border-white/10 text-xs font-semibold inline-flex items-center gap-1" data-testid={`reject-${a.application_id}`}>
                          <XIcon size={12} /> Décliner
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`tag-pill ${a.status === "accepted" ? "bg-green-500/20 border-green-500/40 text-green-300" : "bg-white/5"}`}>
                          {a.status === "accepted" ? "Retenu" : "Décliné"}
                        </span>
                        {a.status === "accepted" && (
                          <button onClick={() => contactApplicant(a)} className="px-3 py-1.5 rounded-full bg-brand-red/20 text-brand-red border border-brand-red/30 text-xs font-semibold inline-flex items-center gap-1" data-testid={`contact-${a.application_id}`}>
                            <MessageCircle size={12} /> Contacter
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isRecruiter && casting.status === "open" && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="apply-panel">
          {myApp ? (
            <div className="text-center py-4">
              <Briefcase size={32} className="mx-auto text-brand-orange mb-3" />
              <p className="font-semibold mb-1">Candidature envoyée</p>
              <p className="text-sm text-white/60 mb-3">
                Statut : <strong className={myApp.status === "accepted" ? "text-green-400" : myApp.status === "rejected" ? "text-red-400" : "text-brand-yellow"}>
                  {myApp.status === "accepted" ? "Retenu 🎉" : myApp.status === "rejected" ? "Non retenu" : "En attente"}
                </strong>
              </p>
            </div>
          ) : (
            <form onSubmit={apply}>
              <h3 className="font-display text-xl font-black mb-4">Candidater à cette opportunité</h3>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message au recruteur (expériences, disponibilités, liens vers vos morceaux...)"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50 min-h-[120px] mb-4"
                data-testid="application-message"
              />
              <button type="submit" disabled={applying} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="submit-application-btn">
                <Send size={14} /> {applying ? "Envoi..." : "Envoyer ma candidature"}
              </button>
              {!user && <p className="text-xs text-white/50 mt-3">Vous devez être connecté pour candidater.</p>}
            </form>
          )}
        </div>
      )}
    </div>
  );
};

const InfoBlock = ({ icon, label, value }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
    <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 inline-flex items-center gap-1">{icon} {label}</p>
    <p className="text-sm font-semibold">{value}</p>
  </div>
);

export default CastingDetailPage;
